/**
 * 请求层 → UI 层的事实广播（无依赖的极小实现）。
 *
 * 分工是刻意的：`api-client` **只广播事实**，不认识任何组件、不引入 Vue；
 * `components/providers/ApiEvents.vue` 是唯一的翻译点，把错误码变成本地状态
 * （登出、倒计时、额度弹窗、提示条）。
 *
 * 事件分两类，名字上就区分开：
 * - `*-fact`：请求层观察到的事实，订阅方是 `ApiEvents.vue`；
 * - `ui:*`：已经决定要做的界面动作（额度变了、开始倒计时），订阅方是各处组件。
 * 混在一起会让"谁负责决定"变得含糊——例如倒计时究竟由错误码触发还是由事件触发。
 *
 * 同步派发（不是微任务），所以"派发后立刻断言"在测试里成立。
 */

import type { QuotaScope } from '@spirit/contracts';

/**
 * 42900 的 `details` 口径（契约 §4.4，v1.3.0 起）。
 *
 * 服务端只报"被拦的是哪一维"（`scope`）+ 该维**剩余百分比** + 重置时刻：
 * 旧口径里的绝对剩余量与上限已经从前端彻底剥离，
 * 所以弹窗只能、也应该只说百分比与何时恢复。
 */
export interface QuotaExceededDetails {
  scope: QuotaScope;
  /** 被拦那一维的剩余百分比（服务端算好的 0-100 整数，通常是 0）。 */
  remainingPercent: number;
  resetAt: string;
}

/** 契约 §5.2 里需要全局反应的事实。 */
export type ApiEvent =
  | ({ type: 'quota-exceeded'; message: string } & QuotaExceededDetails)
  | { type: 'rate-limited'; message: string; retryAfterSeconds: number }
  | { type: 'unauthorized'; message: string }
  | { type: 'upstream-failure'; message: string; notBillable: boolean }
  | { type: 'maintenance'; message: string };

export type ApiEventListener = (event: ApiEvent) => void;

/** UI 侧事件载荷表。 */
export interface UiEventMap {
  /** 额度发生变化（一次问答结算、手动刷新、登录）→ 额度条重新拉取。 */
  'ui:quota-changed': { reason: 'chat' | 'manual' | 'login' };
  /** 请求"开始一个新会话"，由聊天视图响应。 */
  'ui:chat-reset': undefined;
}

type FactHandler = ApiEventListener;
type UiHandler<K extends keyof UiEventMap> = (payload: UiEventMap[K]) => void;

const factListeners = new Set<FactHandler>();
const uiListeners = new Map<keyof UiEventMap, Set<(payload: never) => void>>();

/** 订阅请求层事实；返回退订函数。 */
export function onApiEvent(listener: ApiEventListener): () => void {
  factListeners.add(listener);
  return () => {
    factListeners.delete(listener);
  };
}

/**
 * 广播事实。
 *
 * 复制一份再遍历，并且每个监听器单独 try/catch：
 * 一个写坏的订阅者绝不能中断"40100 → 清理会话"这条链——那会让用户停在一个
 * 拿着失效令牌却毫不知情的状态里。
 */
export function emitApiEvent(event: ApiEvent): void {
  for (const listener of Array.from(factListeners)) {
    try {
      listener(event);
    } catch (error) {
      console.error('[api-events] listener failed', error);
    }
  }
}

/** 订阅 UI 事件；返回退订函数（幂等）。 */
export function onEvent<K extends keyof UiEventMap>(name: K, handler: UiHandler<K>): () => void {
  const bucket = uiListeners.get(name) ?? new Set<(payload: never) => void>();
  uiListeners.set(name, bucket);
  const erased = handler as (payload: never) => void;
  bucket.add(erased);
  return () => {
    const current = uiListeners.get(name);
    if (current === undefined) {
      return;
    }
    current.delete(erased);
    if (current.size === 0) {
      uiListeners.delete(name);
    }
  };
}

/** 广播 UI 事件。 */
export function emitEvent<K extends keyof UiEventMap>(name: K, payload: UiEventMap[K]): void {
  const bucket = uiListeners.get(name);
  if (bucket === undefined) {
    return;
  }
  for (const handler of Array.from(bucket)) {
    try {
      (handler as unknown as UiHandler<K>)(payload);
    } catch (error) {
      console.error(`[events] 监听 ${String(name)} 时出错`, error);
    }
  }
}

/** 仅供测试：清空所有订阅。 */
export function clearEventListeners(): void {
  factListeners.clear();
  uiListeners.clear();
}
